import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Grid,
  Card,
  CardContent,
  Typography,
  makeStyles,
  useTheme,
  Divider,
  Button
} from '@material-ui/core'
import DateFilter from '../../components/shared/dateFilter/dateFilter'
import { DateFilters, GlobalNotification } from '../../model'
import moment from 'moment'
import { RegulatoryReportsTypes } from './regulatoryReports.Model'
import CustomFilter from '../../components/shared/customFilter/customFilter'
import {
  generateRegulatoryReport,
  generateRegulatoryReportAsync,
  getGenerateRegulatoryReport
} from './regulatoryReports.service'
import { styles } from './regulatoryReports.styles'
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { RegulatoryReportsPowerDownloadCard } from './regulatoryReportsPowerDownloadCard'
import { enumKeyToValue, enumValueToKey } from '../../helpers/enumHelper'
import useRetries from '../../hooks/useRetries'
import { NotificationMessageIcons } from '../../components'
import { useErrorDetailListDialog } from '../../hooks'
import { useNotify, AwaitedNotification } from '@globaleris/ui.library'
import { EnumGrupoDocumento } from '../../api'

export interface RegulatoryReportsPageProps {
  commodity: number
  onGlobalNotificationChange: (globalNotification: GlobalNotification | undefined) => void
}

export const RegulatoryReportsGasCommodity: React.FC<RegulatoryReportsPageProps> = props => {
  const [t] = useTranslation(['regulatoryReports'])

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [codageCustomFilter, setCodageCustomFilter] = useState<any>(undefined)
  const [infoClientesCustomFilter, setInfoClientesCustomFilter] = useState<any>(undefined)
  const [infoConsumosCustomFilter, setInfoConsumosCustomFilter] = useState<any>(undefined)

  const [isFetching, setIsFetching] = useState<boolean>(true)

  const [refreshChild, setRefreshChild] = useState<boolean>(true)

  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()

  const { awaited } = useNotify()

  const [codageFilter, setCodageFilter] = useState<any>({
    startDate: moment().subtract(1, 'months').startOf('month').toDate(),
    endDate: moment().subtract(1, 'months').endOf('month').toDate(),
    activeDateFilter: DateFilters.LASTMONTH
  })

  const [infoClientesFilter, setInfoClientesFilter] = useState<any>({
    startDate: moment().subtract(1, 'months').startOf('month').toDate(),
    endDate: moment().subtract(1, 'months').endOf('month').toDate(),
    activeDateFilter: DateFilters.LASTMONTH
  })

  const [infoConsumosFilter, setInfoConsumosFilter] = useState<any>({
    startDate: moment().startOf('year').subtract(1, 'year').toDate(),
    endDate: moment().endOf('year').subtract(1, 'year').toDate(),
    activeDateFilter: DateFilters.LAST_CLOSED_YEAR
  })

  //#region Hooks

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async () => {
      setTimeout(async () => {
        setRefreshChild(prevState => !prevState)
      }, 7000)
    },
    5000,
    {
      autoStart: false,
      immediate: true,
      onRetry: () => {
        props.onGlobalNotificationChange({
          icon: NotificationMessageIcons.LOADING,
          message: t('regulatoryReports:globalNotifications.retries.messages.onRetry'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.cancel'),
              onClick: () => {
                stopRetriesTimer()
                props.onGlobalNotificationChange({
                  message: t('regulatoryReports:globalNotifications.retries.messages.onStop'),
                  actions: [
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
                      onClick: async () => {
                        startRetriesTimer()
                      }
                    },
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.close'),
                      onClick: () => {
                        props.onGlobalNotificationChange(undefined)
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        props.onGlobalNotificationChange({
          icon: NotificationMessageIcons.INFO,
          message: t('regulatoryReports:globalNotifications.retries.messages.onFinish'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            },
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.close'),
              onClick: () => {
                props.onGlobalNotificationChange(undefined)
              }
            }
          ]
        })
      },
      onError: () => {
        props.onGlobalNotificationChange({
          icon: NotificationMessageIcons.ERROR,
          message: t('regulatoryReports:globalNotifications.retries.messages.onError'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            }
          ]
        })
      }
    }
  )

  //#endregion

  //#region Handlers

  const onSelectDateHandler = (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters,
    filterType?: RegulatoryReportsTypes
  ): void => {
    switch (filterType) {
      case RegulatoryReportsTypes.CODAGE:
        setCodageFilter({ startDate, endDate, activeDateFilter })
        setCodageCustomFilter(undefined)
        break
      case RegulatoryReportsTypes.INFOCLIENTES:
        setInfoClientesFilter({ startDate, endDate, activeDateFilter })
        setInfoClientesCustomFilter(undefined)
        break
      case RegulatoryReportsTypes.INFOCONSUMOS:
        setInfoConsumosFilter({ startDate, endDate, activeDateFilter })
        setInfoConsumosCustomFilter(undefined)
        break
      default:
        break
    }
  }

  const onRemitTableChildFetching = (childIsFetching: boolean) => {
    setIsFetching(childIsFetching)
  }

  const onApplyCustomFilter = async (filterToApply: any, filterType: RegulatoryReportsTypes) => {
    switch (filterType) {
      case RegulatoryReportsTypes.CODAGE:
        setCodageFilter({
          startDate: filterToApply.startDate,
          endDate: filterToApply.endDate,
          activeDateFilter: DateFilters.CUSTOM
        })
        setCodageCustomFilter({
          startDate: filterToApply.startDate,
          endDate: filterToApply.endDate
        })
        break
      case RegulatoryReportsTypes.INFOCLIENTES:
        setInfoClientesFilter({
          startDate: filterToApply.startDate,
          endDate: filterToApply.endDate,
          activeDateFilter: DateFilters.CUSTOM
        })
        setInfoClientesCustomFilter({
          startDate: filterToApply.startDate,
          endDate: filterToApply.endDate
        })
        break
      case RegulatoryReportsTypes.INFOCONSUMOS:
        setInfoConsumosFilter({
          startDate: filterToApply.startDate,
          endDate: filterToApply.endDate,
          activeDateFilter: DateFilters.CUSTOM
        })
        setInfoConsumosCustomFilter({
          startDate: filterToApply.startDate,
          endDate: filterToApply.endDate
        })
        break

      default:
        break
    }
  }

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    return true
  }

  //#endregion

  const generateReport = async (reportType: RegulatoryReportsTypes) => {
    let dateFilter
    switch (reportType) {
      case RegulatoryReportsTypes.CODAGE:
        dateFilter = codageFilter
        break
      case RegulatoryReportsTypes.INFOCLIENTES:
        dateFilter = infoClientesFilter
        break
      case RegulatoryReportsTypes.INFOCONSUMOS:
        dateFilter = infoConsumosFilter
        break
      default:
        break
    }

    var request = getGenerateRegulatoryReport(
      dateFilter.startDate,
      dateFilter.endDate,
      reportType,
      props.commodity
    )

    getConfirmation(
      ConfirmationDialogs.GENERATE_REPORT,
      t('confirmDialogs:GENERATE_REPORT.message', {
        startDate: moment(dateFilter.startDate).format('DD/MM/YYYY'),
        endDate: moment(dateFilter.endDate).format('DD/MM/YYYY')
      })
    ).then(async () => {
      closeConfirmation()

      awaited({
        title: t('regulatoryReports:notifications.generateReportAsync.title', {}),
        message: t('regulatoryReports:notifications.generateReportAsync.message'),
        keyInfo: reportType.toString()
      } as AwaitedNotification)

      generateRegulatoryReportAsync(request).then(async response => {
        if (response.success) {
          startRetriesTimer()
        }
      })
    })
  }

  return (
    <Grid container spacing={3}>
      <Grid item md={6} lg={4}>
        <Card style={{ height: '100%' }}>
          <CardContent className={classes.cardContent}>
            <div className={classes.cardContentPadding}>
              <Typography variant="subtitle1">
                {t('regulatoryReports:commodityGas.codageTitle')}
              </Typography>
              <Typography variant="body2" className={classes.description}>
                {t('regulatoryReports:commodityGas.codageDescription')}
              </Typography>
            </div>
            <div>
              <Divider />
              <div className={classes.cardContentPadding}>
                <Grid container>
                  <div className={classes.dateFilterContainer}>
                    <DateFilter
                      inactive={codageCustomFilter}
                      enabled={!isFetching}
                      initialFilter={
                        codageFilter?.activeDateFilter === null ||
                        codageFilter?.activeDateFilter === undefined
                          ? DateFilters.LASTMONTH
                          : codageFilter.activeDateFilter
                      }
                      onSelectDate={(startDate, endDate, activeDateFilter) =>
                        onSelectDateHandler(
                          startDate,
                          endDate,
                          activeDateFilter,
                          RegulatoryReportsTypes.CODAGE
                        )
                      }
                      filters={[
                        {
                          filter: DateFilters.LASTMONTH,
                          label: t('translation:components.dateFilter.lastClosedMonth')
                        }
                      ]}
                    ></DateFilter>
                  </div>
                  <CustomFilter
                    onApplyFilter={filterToApply =>
                      onApplyCustomFilter(filterToApply, RegulatoryReportsTypes.CODAGE)
                    }
                    onPrevalidateFilter={onPrevalidateCustomFilter}
                    appliedFilter={codageCustomFilter}
                    enabled={!isFetching}
                  ></CustomFilter>
                </Grid>
                <Grid container justify="flex-end" className={classes.buttonContainer}>
                  {/* <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      console.log('Ver anteriores');
                    }}
                  >
                    {t('regulatoryReports:commodityPower.seePreviousOption')}
                  </Button> */}
                  <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      generateReport(RegulatoryReportsTypes.CODAGE)
                    }}
                  >
                    {t('regulatoryReports:commodityPower.generateReportOption')}
                  </Button>
                </Grid>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={6} lg={4}>
        <Card style={{ height: '100%' }}>
          <CardContent className={classes.cardContent}>
            <div className={classes.cardContentPadding}>
              <Typography variant="subtitle1">
                {t('regulatoryReports:commodityGas.infoClientesTitle')}
              </Typography>
              <Typography variant="body2" className={classes.description}>
                {t('regulatoryReports:commodityGas.infoClientesDescription')}
              </Typography>
            </div>
            <div>
              <Divider />
              <div className={classes.cardContentPadding}>
                <Grid container>
                  <div className={classes.dateFilterContainer}>
                    <DateFilter
                      inactive={infoClientesCustomFilter}
                      enabled={!isFetching}
                      initialFilter={
                        infoClientesFilter?.activeDateFilter === null ||
                        infoClientesFilter?.activeDateFilter === undefined
                          ? DateFilters.LASTMONTH
                          : infoClientesFilter.activeDateFilter
                      }
                      onSelectDate={(startDate, endDate, activeDateFilter) =>
                        onSelectDateHandler(
                          startDate,
                          endDate,
                          activeDateFilter,
                          RegulatoryReportsTypes.INFOCLIENTES
                        )
                      }
                      filters={[
                        {
                          filter: DateFilters.LASTMONTH,
                          label: t('translation:components.dateFilter.lastClosedMonth')
                        }
                      ]}
                    ></DateFilter>
                  </div>
                  <CustomFilter
                    onApplyFilter={filterToApply =>
                      onApplyCustomFilter(filterToApply, RegulatoryReportsTypes.INFOCLIENTES)
                    }
                    onPrevalidateFilter={onPrevalidateCustomFilter}
                    appliedFilter={infoClientesCustomFilter}
                    enabled={!isFetching}
                  ></CustomFilter>
                </Grid>
                <Grid container justify="flex-end" className={classes.buttonContainer}>
                  {/* <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      console.log('Ver anteriores');
                    }}
                  >
                    {t('regulatoryReports:commodityPower.seePreviousOption')}
                  </Button> */}
                  <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      generateReport(RegulatoryReportsTypes.INFOCLIENTES)
                    }}
                  >
                    {t('regulatoryReports:commodityPower.generateReportOption')}
                  </Button>
                </Grid>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={6} lg={4}>
        <Card style={{ height: '100%' }}>
          <CardContent className={classes.cardContent}>
            <div className={classes.cardContentPadding}>
              <Typography variant="subtitle1">
                {t('regulatoryReports:commodityGas.infoConsumosTitle')}
              </Typography>
              <Typography variant="body2" className={classes.description}>
                {t('regulatoryReports:commodityGas.infoConsumosDescription')}
              </Typography>
            </div>
            <div>
              <Divider />
              <div className={classes.cardContentPadding}>
                <Grid container>
                  <div className={classes.dateFilterContainer}>
                    <DateFilter
                      inactive={false}
                      enabled={!isFetching}
                      initialFilter={
                        infoConsumosFilter?.activeDateFilter === null ||
                        infoConsumosFilter?.activeDateFilter === undefined
                          ? DateFilters.LAST_CLOSED_YEAR
                          : infoConsumosFilter.activeDateFilter
                      }
                      onSelectDate={(startDate, endDate, activeDateFilter) =>
                        onSelectDateHandler(
                          startDate,
                          endDate,
                          activeDateFilter,
                          RegulatoryReportsTypes.INFOCONSUMOS
                        )
                      }
                      filters={[
                        {
                          filter: DateFilters.LAST_CLOSED_YEAR,
                          label: t('translation:components.dateFilter.lastClosedYear')
                        }
                      ]}
                    ></DateFilter>
                  </div>
                  <CustomFilter
                    onApplyFilter={filterToApply =>
                      onApplyCustomFilter(filterToApply, RegulatoryReportsTypes.INFOCONSUMOS)
                    }
                    onPrevalidateFilter={onPrevalidateCustomFilter}
                    appliedFilter={infoConsumosCustomFilter}
                    enabled={!isFetching}
                  ></CustomFilter>
                </Grid>
                <Grid container justify="flex-end" className={classes.buttonContainer}>
                  {/* <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      console.log('Ver anteriores');
                    }}
                  >
                    {t('regulatoryReports:commodityPower.seePreviousOption')}
                  </Button> */}
                  <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      generateReport(RegulatoryReportsTypes.INFOCONSUMOS)
                    }}
                  >
                    {t('regulatoryReports:commodityPower.generateReportOption')}
                  </Button>
                </Grid>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <RegulatoryReportsPowerDownloadCard
          onFetching={onRemitTableChildFetching}
          refreshChild={refreshChild}
          groupDocumentToFilter={[
            enumValueToKey(EnumGrupoDocumento, EnumGrupoDocumento.IIRRCNMCGas)
          ]}
        />
      </Grid>
    </Grid>
  )
}
